import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { SignalCard } from '../components/signals.tsx'
import { Badge, Banner, Card, CardBody, CardHead, LoadError, OddsButton, PageHead, Seg, Skeleton } from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { slipNames } from '../lib/betslip.tsx'
import { useApi } from '../lib/api.ts'
import { gbpCompact, kickOffTime, toKickOff } from '../lib/format.ts'
import { displayRunner } from '../../shared/runners.ts'
import type { GameMarket, GamePayload, GameRunner } from '../../shared/types.ts'

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

/**
 * A game and every market priced on it, grouped — Result, Goals, Handicaps, Correct score,
 * Players, Cards & corners. Each runner shows its bookie price; clicking it puts the
 * selection on the bet slip.
 */
export function Game() {
  const { eventId = '' } = useParams()
  const { data, error, loading, reload } = useApi<GamePayload>(`/games/${encodeURIComponent(eventId)}`, 60_000)
  const [group, setGroup] = useState('All')
  usePageTitle(data?.event ?? 'Game')

  if (error) return <main className="content"><LoadError error={error} onRetry={reload} /></main>
  if (loading || !data) {
    return (
      <main className="content">
        <Card>
          <CardBody>
            <Skeleton rows={10} />
          </CardBody>
        </Card>
      </main>
    )
  }

  const kick = toKickOff(data.kickoff)
  const shown = group === 'All' ? data.groups : data.groups.filter((g) => g.name === group)
  const count = data.groups.reduce((t, g) => t + g.markets.length, 0)

  return (
    <main className="content">
      <PageHead
        breadcrumb={
          <>
            <Link to="/">Today</Link> / {data.event}
          </>
        }
        title={data.event}
        sub={[
          data.competition,
          data.kickoff ? `${day(data.kickoff)} ${kickOffTime(data.kickoff)} · ${kick.label}` : null,
          `${count} market${count === 1 ? '' : 's'}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      />

      <div className="grid cols-12" style={{ alignItems: 'start' }}>
        <div className={`${data.signals.length > 0 ? 'span-8' : 'span-12'} stack`}>
          {data.closed && (
            <Banner tone="halt" icon="■" title="Betting closed — this game has kicked off">
              Bets are taken before kick-off only; there is no in-play betting. The last prices before kick-off are shown
              for reference.
            </Banner>
          )}
          {count === 0 ? (
            <Card>
              <CardBody>
                <div className="u-faint" style={{ padding: '12px 0' }}>
                  No prices for this game yet. Games are priced from about three days before kick-off.
                </div>
              </CardBody>
            </Card>
          ) : (
            <>
              <div>
                <Seg
                  label="Market group"
                  value={group}
                  onChange={setGroup}
                  options={[
                    { value: 'All', label: 'All' },
                    ...data.groups.map((g) => ({ value: g.name, label: `${g.name} ${g.markets.length}` })),
                  ]}
                />
              </div>
              <div className="stack">
                {shown.map((g) => (
                  <Card key={g.name}>
                    <CardHead title={g.name} />
                    <CardBody flush>
                      {g.markets.map((m, i) => (
                        <MarketBlock key={m.marketId} event={data.event} market={m} last={i === g.markets.length - 1} closed={data.closed} />
                      ))}
                    </CardBody>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>

        {data.signals.length > 0 && (
          <div className="span-4 stack">
            {/* Headed like the market groups on the left, so the two columns' cards start level. */}
            <div>
              <div className="seg" title="Prices that moved a long way in a short time">
                <span className="opt active">Moving now {data.signals.length}</span>
              </div>
            </div>
            <Card>
              <CardBody className="stack sm">
                {data.signals.map((s) => (
                  <SignalCard key={`${s.marketId}:${s.selectionId}:${s.kind}`} signal={s} showEvent={false} />
                ))}
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </main>
  )
}

function MarketBlock({ event, market: m, last, closed }: { event: string; market: GameMarket; last: boolean; closed: boolean }) {
  return (
    <div style={{ padding: '10px 16px 12px', borderBottom: last ? undefined : '1px solid var(--border)' }}>
      <div className="between" style={{ marginBottom: 6 }}>
        <Link to={`/markets/${encodeURIComponent(m.marketId)}`} className="cell-strong" style={{ color: 'inherit' }}>
          {m.name}
        </Link>
        <div className="row" style={{ gap: 8 }}>
          {m.signals > 0 && <Badge tone="teal">{m.signals} moving</Badge>}
          {closed ? <Badge tone="neg">Closed</Badge> : m.suspended && <Badge tone="warn">Suspended</Badge>}
          {m.totalMatched ? <span className="cell-sub u-mono">{gbpCompact(m.totalMatched)} matched</span> : null}
        </div>
      </div>
      {/* Two- and three-way markets (match odds, over/under, double chance) sit on one line. */}
      <div className={`runner-grid${m.runners.length === 2 || m.runners.length === 3 ? ` n${m.runners.length}` : ''}`}>
        {m.runners.map((r) => (
          <RunnerRow key={r.selectionId} event={event} market={m} runner={r} disabled={closed || m.suspended} />
        ))}
      </div>
    </div>
  )
}

function RunnerRow({
  event,
  market: m,
  runner: r,
  disabled,
}: {
  event: string
  market: GameMarket
  runner: GameRunner
  disabled: boolean
}) {
  return (
    <div className="runner-row">
      <span className="runner-name">{displayRunner(r.name)}</span>
      <OddsButton
        disabled={disabled}
        selection={{
          marketId: m.marketId,
          selectionId: r.selectionId,
          event,
          ...slipNames(r.name, m.name),
          price: r.price,
        }}
      />
    </div>
  )
}
